
// The root route's error boundary content (router/RouteErrorBoundary.tsx);
// it was app/global-error.tsx, the App Router's root-level error boundary. It catches
// errors that React threw during render that NO other error.tsx caught —
// including ChunkLoadError from a lazy import. Once React catches the error,
// it does NOT bubble through window.onerror, so the window-level handler in
// ChunkErrorReloader.tsx wouldn't see it. This boundary is the React-side
// counterpart.
//
// Behavior:
//   - ChunkLoadError → window.location.reload() (with the same 10s loop-guard
//     as ChunkErrorReloader so a real bug doesn't infinite-loop).
//   - Any other error → render Next.js's default "Try again" fallback so the
//     user can retry and optionally see the error message.

import { useEffect } from "react";

import { isChunkLoadError } from "@/lib/chunkLoadError";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    if (!isChunkError) return;
    const RELOAD_KEY = "veloxp.chunkErrorReloadAt";
    const COOLDOWN_MS = 10_000;
    try {
      const last = sessionStorage.getItem(RELOAD_KEY);
      if (last && Date.now() - Number(last) < COOLDOWN_MS) {
        // Don't infinite-loop on a real bug; let the user see the error.
        console.warn("[global-error] ChunkLoadError again within 10s; staying put.");
        return;
      }
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    console.warn("[global-error] ChunkLoadError — reloading.");
    window.location.reload();
  }, [isChunkError]);

  return (
    <div
        style={{
          margin: 0,
          padding: 0,
          background: "#0A0E17",
          color: "#F8FAFC",
          fontFamily: "system-ui, -apple-system, sans-serif",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ maxWidth: 520, padding: "2rem", textAlign: "center" }}>
          {isChunkError ? (
            <>
              <h1 style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>Reloading…</h1>
              <p style={{ opacity: 0.7, fontSize: "0.875rem" }}>
                A new version was deployed while this tab was open. Refreshing to pick it up.
              </p>
            </>
          ) : (
            <>
              <h1 style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>Something went wrong</h1>
              <p style={{ opacity: 0.7, fontSize: "0.875rem", marginBottom: "1rem" }}>
                {error.message || "An unexpected error occurred."}
              </p>
              <button
                onClick={() => reset()}
                style={{
                  background: "#06B6D4",
                  color: "white",
                  border: "none",
                  borderRadius: "0.375rem",
                  padding: "0.5rem 1rem",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                Try again
              </button>
            </>
          )}
        </div>
    </div>
  );
}
